import { supabase } from '../../common/config/supabase';
import { OrderDirection } from '../../common/enums/order-direction.enum';
import { CompanyStatus } from '../../common/interfaces/company.interface';
import { IPaginatedEntity } from '../../common/interfaces/paginated-entity.interface';
import {
  IProfile,
  IProfileCreateDTO,
  ProfileStatus,
} from '../../common/interfaces/profile.interface';
import { UserRoleEnum } from '../../common/interfaces/user-profile.interface';

export const getUserProfile = async (id: string): Promise<IProfile> => {
  const { data, error } = await supabase
    .from('profiles')
    .select(`*, company:companies(id, status)`)
    .eq('id', id)
    .single();

  if (
    (data?.role !== UserRoleEnum.SUPER_ADMIN &&
      (data.company?.status === CompanyStatus.INACTIVE ||
        data.status === ProfileStatus.INACTIVE)) ||
    !data
  ) {
    await supabase.auth.signOut();
    window.location.href = '/';
  }

  if (error) {
    throw error;
  }

  return data;
};

export const getProfiles = async (
  limit: number,
  page: number,
  search?: string,
  orderBy?: string,
  orderDirection?: OrderDirection,
  status?: ProfileStatus,
  companyId?: string,
): Promise<IPaginatedEntity<IProfile>> => {
  const { data, error } = await supabase.rpc('get_profiles', {
    search_term: search || '',
    status_filter: status,
    limit_val: limit,
    page_val: page,
    order_direction: orderDirection,
    order_by: orderBy,
    company_id_val: +(companyId || ''),
  });

  if (error) {
    throw error;
  }

  return {
    items: data,
    meta: {
      currentPage: page,
      itemCount: data.length,
      itemsPerPage: limit,
      totalItems: data[0] ? data[0].total_count : 0,
      totalPages: data[0] ? Math.ceil(+data[0].total_count / limit) : 0,
      orderByColumn: orderBy,
      orderDirection: orderDirection,
    },
  };
};

export const addProfile = async (profile: IProfileCreateDTO): Promise<IProfile | null> => {
  const { data, error } = await supabase.functions.invoke('add-user', {
    body: {
      email: profile.email,
      phone: profile.phone,
      details: profile.details,
      role: profile.role,
      status: profile.status,
      company_id: profile.company_id,
    },
  });

  if (error) {
    throw error;
  }

  return data;
};

export const deleteProfile = async (id: string): Promise<IProfile | null> => {
  const { data, error } = await supabase.functions.invoke('delete-user', {
    body: { id },
  });

  if (error) {
    throw error;
  }

  return data;
};

export const updateProfile = async (profile: IProfileCreateDTO): Promise<IProfile | null> => {
  if (!profile.id) {
    return null;
  }

  const { data, error } = await supabase
    .from('profiles')
    .update({
      phone: profile.phone,
      details: profile.details,
      role: profile.role,
      status: profile.status,
    })
    .eq('id', profile.id);

  if (error) {
    throw error;
  }

  return data;
};
